import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FlightFilterType } from 'constants/flight'

export function getFlightFilterLabel(intl: IntlShape, filterType: string): string {
  switch (filterType) {
    case FlightFilterType.STOPS:
      return intl.formatMessage({
        id: 'K84Yu8jxhzhG63ZWarEk',
        defaultMessage: 'Stops',
        description: 'Flight filter label for number of stops in the flight search or booking flow',
      })
    case FlightFilterType.AIRLINES:
      return intl.formatMessage({
        id: 'wkCJftBBBsQFOKsnYh-F',
        defaultMessage: 'Airlines',
        description: 'Flight filter label for airline selection in the flights flow',
      })
    case FlightFilterType.STOP_DURATIONS:
      return intl.formatMessage({
        id: 'Aa5iL9bqMiplIDCNkl24',
        defaultMessage: 'Stopover time',
        description: 'Flight filter label for stopover duration in the flights flow',
      })
    case FlightFilterType.CABIN_TYPES:
      return intl.formatMessage({
        id: 'bPRbwvO76tpiKetu1dSD',
        defaultMessage: 'Cabin',
        description: 'Flight filter label for cabin class selection in the flights section',
      })
    case FlightFilterType.DURATIONS:
      return intl.formatMessage({
        id: 'mMuAK41SDCFsKhlS8tey',
        defaultMessage: 'Duration',
        description: 'Flight filter label for flight duration in the flight search or booking flow',
      })
    case FlightFilterType.PRICES:
      return intl.formatMessage({
        id: 'JiYh6GiOiiXw6ZkJq-8p',
        defaultMessage: 'Price',
        description: 'Flight filter label for price range in the redesigned flight search filter panel',
      })
    case FlightFilterType.TIMES:
      return intl.formatMessage({
        id: 'eRZJ5-HzQ3c5mgDqYA-6',
        defaultMessage: 'Times',
        description: 'Flight filter label for departure/arrival times in the flights section',
      })
    case FlightFilterType.BAGS:
      return intl.formatMessage({
        id: '73SVYIfDEZ-GaNLtHMzX',
        defaultMessage: 'Bags',
        description: 'Flight filter label for baggage options in the flight search or booking flow',
      })
    case FlightFilterType.MEMBER_PRICES:
      return intl.formatMessage({
        id: 'pjM-j2wgHZSUzn7bmabJ',
        defaultMessage: 'LuxPlus',
        description: 'Flight filter label for LuxPlus member pricing options in the flights flow',
      })
    case FlightFilterType.STOP_OVER_CITIES:
      return intl.formatMessage({
        id: '0xNUpByF-xRkbDmLvKov',
        defaultMessage: 'Stopover cities',
        description: 'Flight filter label for stopover city selection in the flights section',
      })
    case FlightFilterType.ALLIANCES:
      return intl.formatMessage({
        id: '9j2ILO0aHLk8uchC_1Km',
        defaultMessage: 'Alliance',
        description: 'Flight filter dropdown label for selecting airline alliance groups in the redesigned filter panel',
      })
    case FlightFilterType.RECOMMENDED:
      return intl.formatMessage({
        id: '6rlXlAYARUJTptemDPfQ',
        defaultMessage: 'Recommended',
        description: 'Flight filter section label for recommended filters in the redesigned filter panel',
      })
    case FlightFilterType.APPLIED_FILTERS:
      return intl.formatMessage({
        id: 'PEUoj680xvxQUcsG2RIr',
        defaultMessage: 'Applied filters',
        description: 'Flight filter label for currently applied filters in the flights section',
      })
    default:
      return filterType
  }
}

export function getFlightFilterModalLabel(intl: IntlShape, filterType: string): string {
  if (filterType === FlightFilterType.DURATIONS) {
    return intl.formatMessage({
      id: '56ESkKjoWIq3of529Lrd',
      defaultMessage: 'Flight duration',
      description: 'Flight filter modal label for flight duration filter',
    })
  }
  if (filterType === FlightFilterType.RECOMMENDED) {
    return getFlightFilterLabel(intl, FlightFilterType.RECOMMENDED)
  }
  return getFlightFilterLabel(intl, filterType)
}

function useFlightFilterLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const flightFiltersLabelMap: Record<string, string> = {
      [FlightFilterType.STOPS]: getFlightFilterLabel(intl, FlightFilterType.STOPS),
      [FlightFilterType.AIRLINES]: getFlightFilterLabel(intl, FlightFilterType.AIRLINES),
      [FlightFilterType.STOP_DURATIONS]: getFlightFilterLabel(intl, FlightFilterType.STOP_DURATIONS),
      [FlightFilterType.CABIN_TYPES]: getFlightFilterLabel(intl, FlightFilterType.CABIN_TYPES),
      [FlightFilterType.DURATIONS]: getFlightFilterLabel(intl, FlightFilterType.DURATIONS),
      [FlightFilterType.PRICES]: getFlightFilterLabel(intl, FlightFilterType.PRICES),
      [FlightFilterType.TIMES]: getFlightFilterLabel(intl, FlightFilterType.TIMES),
      [FlightFilterType.BAGS]: getFlightFilterLabel(intl, FlightFilterType.BAGS),
      [FlightFilterType.MEMBER_PRICES]: getFlightFilterLabel(intl, FlightFilterType.MEMBER_PRICES),
      [FlightFilterType.STOP_OVER_CITIES]: getFlightFilterLabel(intl, FlightFilterType.STOP_OVER_CITIES),
      [FlightFilterType.ALLIANCES]: getFlightFilterLabel(intl, FlightFilterType.ALLIANCES),
      [FlightFilterType.APPLIED_FILTERS]: getFlightFilterLabel(intl, FlightFilterType.APPLIED_FILTERS),
      [FlightFilterType.RECOMMENDED]: getFlightFilterLabel(intl, FlightFilterType.RECOMMENDED),
    }

    const flightFiltersModalLabelMap: Record<string, string> = {
      [FlightFilterType.STOPS]: getFlightFilterModalLabel(intl, FlightFilterType.STOPS),
      [FlightFilterType.AIRLINES]: getFlightFilterModalLabel(intl, FlightFilterType.AIRLINES),
      [FlightFilterType.STOP_DURATIONS]: getFlightFilterModalLabel(intl, FlightFilterType.STOP_DURATIONS),
      [FlightFilterType.CABIN_TYPES]: getFlightFilterModalLabel(intl, FlightFilterType.CABIN_TYPES),
      [FlightFilterType.DURATIONS]: getFlightFilterModalLabel(intl, FlightFilterType.DURATIONS),
      [FlightFilterType.PRICES]: getFlightFilterModalLabel(intl, FlightFilterType.PRICES),
      [FlightFilterType.TIMES]: getFlightFilterModalLabel(intl, FlightFilterType.TIMES),
      [FlightFilterType.RECOMMENDED]: getFlightFilterModalLabel(intl, FlightFilterType.RECOMMENDED),
      [FlightFilterType.BAGS]: getFlightFilterModalLabel(intl, FlightFilterType.BAGS),
      [FlightFilterType.MEMBER_PRICES]: getFlightFilterModalLabel(intl, FlightFilterType.MEMBER_PRICES),
      [FlightFilterType.STOP_OVER_CITIES]: getFlightFilterModalLabel(intl, FlightFilterType.STOP_OVER_CITIES),
      [FlightFilterType.ALLIANCES]: getFlightFilterModalLabel(intl, FlightFilterType.ALLIANCES),
      [FlightFilterType.APPLIED_FILTERS]: getFlightFilterModalLabel(intl, FlightFilterType.APPLIED_FILTERS),
    }

    const flightFilters: Array<{ label: string; value: FlightFilterType }> = [
      { label: flightFiltersLabelMap[FlightFilterType.STOPS], value: FlightFilterType.STOPS },
      { label: flightFiltersLabelMap[FlightFilterType.AIRLINES], value: FlightFilterType.AIRLINES },
      { label: flightFiltersLabelMap[FlightFilterType.STOP_DURATIONS], value: FlightFilterType.STOP_DURATIONS },
      { label: flightFiltersLabelMap[FlightFilterType.CABIN_TYPES], value: FlightFilterType.CABIN_TYPES },
      { label: flightFiltersLabelMap[FlightFilterType.DURATIONS], value: FlightFilterType.DURATIONS },
      { label: flightFiltersLabelMap[FlightFilterType.BAGS], value: FlightFilterType.BAGS },
      { label: flightFiltersLabelMap[FlightFilterType.MEMBER_PRICES], value: FlightFilterType.MEMBER_PRICES },
      { label: flightFiltersLabelMap[FlightFilterType.STOP_OVER_CITIES], value: FlightFilterType.STOP_OVER_CITIES },
      { label: flightFiltersLabelMap[FlightFilterType.ALLIANCES], value: FlightFilterType.ALLIANCES },
    ]

    return {
      flightFiltersLabelMap,
      flightFiltersModalLabelMap,
      flightFilters,
    }
  }, [intl])
}

export default useFlightFilterLabels
